import { Helmet } from 'react-helmet'
import { APP } from '@/common/strings'
import SecuritiesGrid from './components/SecuritiesGrid'
import { FIND_STOCKS_COPY } from '@/locales/en'

import { EtfsGrid } from './components/EtfsGrid'
import useFindStocksController from './hooks/useFindStocksController'
import { MultiSelect } from '@/common/ui/MultiSelect'
import { Button } from '@/common/ui/button'
import { PieChart, TrendingUp } from 'lucide-react'
import { H1 } from '@/common/ui/h1'
import useIsMobile from '@/common/hooks/useIsMobile'

export function FindStocks() {
    const isMobile = useIsMobile()
    const controller = useFindStocksController()
    const {
        dataSource,
        setDataSource,
        positiveOptions,
        negativeOptions,
        onPositiveChange,
        onNegativeChange,
        filteredSecurities,
        isLoading,
        positiveFilterCount,
        positiveSelected,
        negativeSelected,
        etfsPagination,
        onFiltersClose,
    } = controller

    return (
        <div>
            <Helmet>
                <title>
                    {FIND_STOCKS_COPY.title} | {APP.title}
                </title>
            </Helmet>

            <div className="border-b py-6 self-stretch inline-flex flex-col justify-start items-start gap-6 w-full">
                <H1 className="mb-0 md:text-40 font-normal">{FIND_STOCKS_COPY.title}</H1>
                <div className="inline-flex justify-start items-start gap-4">
                    <div className="mb-4 sm:mb-0 flex items-center gap-2">
                        <Button
                            variant={dataSource === 'etfs' ? 'secondary' : 'outline'}
                            size="sm"
                            onClick={() => setDataSource('etfs')}
                        >
                            <PieChart className="mr-1" size={16} />
                            {FIND_STOCKS_COPY.etfs}
                        </Button>
                        <Button
                            variant={dataSource === 'stocks' ? 'secondary' : 'outline'}
                            size="sm"
                            onClick={() => setDataSource('stocks')}
                        >
                            <TrendingUp className="mr-1" size={16} />
                            {FIND_STOCKS_COPY.stocks}
                        </Button>
                    </div>
                </div>

                <div className="relative w-full h-10">
                    <div className="absolute -inset-x-6">
                        <div className="w-full px-6 overflow-x-auto overflow-y-hidden self-stretch flex flex-row justify-start items-stretch gap-4 md:gap-6">
                            <div className="w-full flex-1">
                                <MultiSelect
                                    maxCount={1}
                                    title={FIND_STOCKS_COPY.includedTitle}
                                    placeholder={isMobile ? '' : FIND_STOCKS_COPY.includedPlaceholder}
                                    label={
                                        isMobile && positiveSelected.length > 0
                                            ? FIND_STOCKS_COPY.includedSmall
                                            : FIND_STOCKS_COPY.included
                                    }
                                    options={positiveOptions}
                                    onValueChange={onPositiveChange}
                                    onCloseSelection={onFiltersClose}
                                    value={positiveSelected}
                                    closeLabel={
                                        dataSource === 'etfs' ? FIND_STOCKS_COPY.showEtfs : FIND_STOCKS_COPY.showStocks
                                    }
                                    clearLabel={FIND_STOCKS_COPY.clear}
                                />
                            </div>
                            <div className="w-full flex-1">
                                <MultiSelect
                                    title={FIND_STOCKS_COPY.excludedTitle}
                                    maxCount={1}
                                    placeholder={isMobile ? '' : FIND_STOCKS_COPY.excludedPlaceholder}
                                    label={
                                        isMobile && negativeSelected.length > 0
                                            ? FIND_STOCKS_COPY.excludedSmall
                                            : FIND_STOCKS_COPY.excluded
                                    }
                                    options={negativeOptions}
                                    onValueChange={onNegativeChange}
                                    onCloseSelection={onFiltersClose}
                                    value={negativeSelected}
                                    closeLabel={
                                        dataSource === 'etfs' ? FIND_STOCKS_COPY.showEtfs : FIND_STOCKS_COPY.showStocks
                                    }
                                    clearLabel={FIND_STOCKS_COPY.clear}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {dataSource === 'stocks' ? (
                <SecuritiesGrid
                    filteredSecurities={filteredSecurities}
                    isLoading={isLoading}
                    dataSource={dataSource}
                    stocksKey={'stocks'}
                    positiveFilterCount={positiveFilterCount}
                />
            ) : (
                <EtfsGrid
                    filteredEtfs={filteredSecurities}
                    isLoading={isLoading}
                    dataSource={dataSource}
                    etfsKey={'etfs'}
                    positiveFilterCount={positiveFilterCount}
                    pagination={etfsPagination}
                />
            )}
        </div>
    )
}
